import { APIResponse, ASRProvider, Session, Segment, Summary } from '~/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Create a new recording session
 */
export async function createSession(
  provider: ASRProvider,
  title?: string
): Promise<{ session_id: string; status: string }> {
  const response = await fetch(`${API_BASE_URL}/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      asr_provider: provider,
      title,
      language: 'zh-CN',
      client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });

  const result = (await response.json()) as APIResponse;

  if (!result.ok) {
    throw new Error(result.error?.message || 'Failed to create session');
  }

  return result.data;
}

/**
 * Update session status
 */
export async function updateSession(
  sessionId: string,
  status: 'recording' | 'paused'
): Promise<Session> {
  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });

  const result = (await response.json()) as APIResponse;

  if (!result.ok) {
    throw new Error(result.error?.message || 'Failed to update session');
  }

  return result.data;
}

/**
 * Upload segment to server
 */
export async function uploadSegment(
  sessionId: string,
  segmentIndex: number,
  startMs: number,
  endMs: number,
  sha256: string,
  audioBlob: Blob
): Promise<void> {
  const formData = new FormData();
  formData.append('segment_index', segmentIndex.toString());
  formData.append('start_ms', startMs.toString());
  formData.append('end_ms', endMs.toString());
  formData.append('sha256', sha256);
  formData.append('audio', audioBlob, `segment-${segmentIndex}.wav`);

  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/segments`, {
    method: 'POST',
    headers: {
      'Idempotency-Key': `${sessionId}:${segmentIndex}:${sha256}`,
    },
    body: formData,
  });

  const result = (await response.json()) as APIResponse;

  if (!result.ok) {
    throw new Error(result.error?.message || 'Failed to upload segment');
  }
}

/**
 * Get segments for session
 */
export async function getSegments(
  sessionId: string,
  fromIndex = 0
): Promise<{ segments: Segment[]; latest_index: number }> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/segments?from_index=${fromIndex}`
  );

  const result = (await response.json()) as APIResponse;

  if (!result.ok) {
    throw new Error(result.error?.message || 'Failed to get segments');
  }

  return result.data;
}

/**
 * Finalize session and trigger summary
 */
export async function finalizeSession(
  sessionId: string
): Promise<{ summary_id: string; status: string }> {
  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/finalize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      summary_style: 'meeting',
      language: 'zh-CN',
    }),
  });

  const result = (await response.json()) as APIResponse;

  if (!result.ok) {
    throw new Error(result.error?.message || 'Failed to finalize session');
  }

  return result.data;
}

/**
 * Get summary for session
 */
export async function getSummary(sessionId: string): Promise<Summary> {
  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/summary`);

  const result = (await response.json()) as APIResponse;

  if (!result.ok) {
    throw new Error(result.error?.message || 'Failed to get summary');
  }

  return result.data;
}
